import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import html2pdf from 'html2pdf.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`termene-tabpanel-${index}`}
      aria-labelledby={`termene-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CalculatorTermene = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // State pentru calculator individual
  const [dataReferinta, setDataReferinta] = useState(null);
  const [tipTermen, setTipTermen] = useState('apel');
  const [rezultatTermen, setRezultatTermen] = useState(null);
  
  // State pentru calculator multiplu
  const [dataReferintaMultiplu, setDataReferintaMultiplu] = useState(null);
  const [termeneSelectate, setTermeneSelectate] = useState([]);
  const [rezultateMultiple, setRezultateMultiple] = useState([]);
  
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Definirea termenelor legale
  const termene = {
    // Termene procesuale civile
    apel: { zile: 30, descriere: 'Apel', tip: 'Proces civil', bazaLegala: 'Art. 304 NCPC' },
    recurs: { zile: 15, descriere: 'Recurs', tip: 'Proces civil', bazaLegala: 'Art. 488 NCPC' },
    contestatie: { zile: 15, descriere: 'Contestație', tip: 'Proces civil', bazaLegala: 'Art. 276 NCPC' },
    opozitie: { zile: 15, descriere: 'Opoziție', tip: 'Proces civil', bazaLegala: 'Art. 417 NCPC' },
    revizuire: { zile: 30, descriere: 'Cerere de revizuire', tip: 'Proces civil', bazaLegala: 'Art. 509 NCPC' },
    
    // Termene procesuale penale
    apel_penal: { zile: 10, descriere: 'Apel penal', tip: 'Proces penal', bazaLegala: 'Art. 408 NCPP' },
    recurs_penal: { zile: 10, descriere: 'Recurs penal', tip: 'Proces penal', bazaLegala: 'Art. 434 NCPP' },
    
    // Termene administrative
    contestatie_administrativa: { zile: 30, descriere: 'Contestație act administrativ', tip: 'Contencios administrativ', bazaLegala: 'Art. 8 Legea 554/2004' },
    plangere_prealabila: { zile: 30, descriere: 'Plângere prealabilă', tip: 'Contencios administrativ', bazaLegala: 'Art. 7 Legea 554/2004' },
    raspuns_plangere: { zile: 45, descriere: 'Răspuns la plângere prealabilă', tip: 'Contencios administrativ', bazaLegala: 'Art. 7 Legea 554/2004' },
    
    // Termene comerciale
    contestatie_executare: { zile: 15, descriere: 'Contestație executare silită', tip: 'Proces comercial', bazaLegala: 'Art. 712 NCPC' },
    opozitie_ordonanta: { zile: 5, descriere: 'Opoziție la ordonanță președințială', tip: 'Proces comercial', bazaLegala: 'Art. 997 NCPC' },
    anulare_arbitraj: { zile: 90, descriere: 'Cerere anulare hotărâre arbitrală', tip: 'Arbitraj', bazaLegala: 'Art. 608 NCPC' },
    
    // Termene fiscale
    contestatie_fiscala: { zile: 30, descriere: 'Contestație fiscală', tip: 'Contencios fiscal', bazaLegala: 'Art. 207 Cod fiscal' },
    plangere_fiscala: { zile: 45, descriere: 'Plângere împotriva deciziei', tip: 'Contencios fiscal', bazaLegala: 'Art. 278 Cod fiscal' }
  };

  // Sărbători legale fixe (fără Paște și Rusalii care variază)
  const sarbatoriFixe = [
    { luna: 0, zi: 1 }, // 1 ianuarie
    { luna: 0, zi: 2 }, // 2 ianuarie
    { luna: 0, zi: 6 }, // Bobotează
    { luna: 4, zi: 1 }, // 1 mai
    { luna: 7, zi: 15 }, // Adormirea Maicii Domnului
    { luna: 10, zi: 30 }, // Sf. Andrei
    { luna: 11, zi: 1 }, // 1 decembrie
    { luna: 11, zi: 25 }, // Crăciun
    { luna: 11, zi: 26 } // 26 decembrie
  ];

  // Funcție pentru calcularea zilelor lucrătoare
  const calculeazaZileLucratoare = (dataStart, numarZile) => {
    let dataCalculata = new Date(dataStart);
    let zileAdaugate = 0;
    
    while (zileAdaugate < numarZile) {
      dataCalculata.setDate(dataCalculata.getDate() + 1);
      
      // Verifică dacă este zi lucrătoare (nu weekend și nu sărbătoare)
      const ziSaptamana = dataCalculata.getDay();
      const esteWeekend = ziSaptamana === 0 || ziSaptamana === 6;
      
      const esteSarbatoare = sarbatoriFixe.some(sarbatoare => 
        dataCalculata.getMonth() === sarbatoare.luna && 
        dataCalculata.getDate() === sarbatoare.zi
      );
      
      if (!esteWeekend && !esteSarbatoare) {
        zileAdaugate++;
      }
    }
    
    return dataCalculata;
  };

  // Calculează termenul individual
  useEffect(() => {
    if (dataReferinta && tipTermen) {
      const termenInfo = termene[tipTermen];
      const dataLimita = calculeazaZileLucratoare(dataReferinta, termenInfo.zile);
      
      // Calculează zilele rămase
      const azi = new Date();
      const diferentaMs = dataLimita.getTime() - azi.getTime();
      const zileRamase = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
      
      let statusTermen = 'normal';
      if (zileRamase < 0) {
        statusTermen = 'expirat';
      } else if (zileRamase <= 3) {
        statusTermen = 'urgent';
      } else if (zileRamase <= 7) {
        statusTermen = 'atentie';
      }
      
      setRezultatTermen({
        tipTermen: termenInfo.descriere,
        tip: termenInfo.tip,
        bazaLegala: termenInfo.bazaLegala,
        numarZile: termenInfo.zile,
        dataReferinta: dataReferinta.toLocaleDateString('ro-RO'),
        dataLimita: dataLimita.toLocaleDateString('ro-RO'),
        zileRamase: zileRamase,
        statusTermen: statusTermen
      });
    } else {
      setRezultatTermen(null);
    }
  }, [dataReferinta, tipTermen]);

  // Calculează termene multiple
  useEffect(() => {
    if (dataReferintaMultiplu && termeneSelectate.length > 0) {
      const rezultate = termeneSelectate.map(tipTermen => {
        const termenInfo = termene[tipTermen];
        const dataLimita = calculeazaZileLucratoare(dataReferintaMultiplu, termenInfo.zile);
        
        const azi = new Date();
        const diferentaMs = dataLimita.getTime() - azi.getTime();
        const zileRamase = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
        
        let statusTermen = 'normal';
        if (zileRamase < 0) {
          statusTermen = 'expirat';
        } else if (zileRamase <= 3) {
          statusTermen = 'urgent';
        } else if (zileRamase <= 7) {
          statusTermen = 'atentie';
        }
        
        return {
          tipTermen: termenInfo.descriere,
          tip: termenInfo.tip,
          bazaLegala: termenInfo.bazaLegala,
          numarZile: termenInfo.zile,
          dataLimita: dataLimita,
          dataLimitaFormatata: dataLimita.toLocaleDateString('ro-RO'),
          zileRamase: zileRamase,
          statusTermen: statusTermen
        };
      });
      
      // Sortează după data limită
      rezultate.sort((a, b) => a.dataLimita - b.dataLimita);
      setRezultateMultiple(rezultate);
    } else {
      setRezultateMultiple([]);
    }
  }, [dataReferintaMultiplu, termeneSelectate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const resetCalculatorIndividual = () => {
    setDataReferinta(null);
    setTipTermen('apel');
    setRezultatTermen(null);
  };

  const resetCalculatorMultiplu = () => {
    setDataReferintaMultiplu(null);
    setTermeneSelectate([]);
    setRezultateMultiple([]);
  };

  const copiazaRezultat = (rezultat) => {
    if (rezultat) {
      const textDeCopiat = `${rezultat.tipTermen}: Data limită ${rezultat.dataLimita} (${rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'}). Baza legală: ${rezultat.bazaLegala}`;
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = (rezultate, tip) => {
    if ((tip === 'individual' && rezultatTermen) || (tip === 'multiplu' && rezultate.length > 0)) {
      const element = document.createElement('div');
      const dataExport = tip === 'individual' ? [rezultatTermen] : rezultate;
      
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            Calculator Termene Procesuale
          </h2>
          
          <div style="margin: 20px 0;">
            ${dataExport.map(rezultat => `
              <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
                <h3 style="color: #333; margin-bottom: 10px;">${rezultat.tipTermen}</h3>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Tip:</strong></span>
                  <span>${rezultat.tip}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Data de referință:</strong></span>
                  <span>${tip === 'individual' ? rezultat.dataReferinta : dataReferintaMultiplu.toLocaleDateString('ro-RO')}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Termen legal:</strong></span>
                  <span>${rezultat.numarZile} zile lucrătoare</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Data limită:</strong></span>
                  <span style="font-weight: bold; color: ${rezultat.statusTermen === 'expirat' ? '#dc3545' : rezultat.statusTermen === 'urgent' ? '#fd7e14' : '#28a745'};">
                    ${tip === 'individual' ? rezultat.dataLimita : rezultat.dataLimitaFormatata}
                  </span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Status:</strong></span>
                  <span style="font-weight: bold; color: ${rezultat.statusTermen === 'expirat' ? '#dc3545' : rezultat.statusTermen === 'urgent' ? '#fd7e14' : '#28a745'};">
                    ${rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'}
                  </span>
                </div>
                
                <div style="margin-top: 10px; padding: 10px; background-color: #f8f9fa; border-radius: 3px;">
                  <strong>Baza legală:</strong> ${rezultat.bazaLegala}
                </div>
              </div>
            `).join('')}
          </div>
          
          <div style="margin-top: 30px; padding: 15px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px;">
            <p style="margin: 0; font-size: 12px; color: #856404;">
              <strong>Notă:</strong> Calculul exclude zilele de weekend și sărbătorile legale. 
              Verificați întotdeauna termenele cu legislația în vigoare și particularitățile fiecărui caz.
            </p>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: `termene-${tip}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expirat': return '#dc3545';
      case 'urgent': return '#fd7e14';
      case 'atentie': return '#ffc107';
      default: return '#28a745';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'expirat': return <WarningIcon sx={{ color: '#dc3545' }} />;
      case 'urgent': return <WarningIcon sx={{ color: '#fd7e14' }} />;
      case 'atentie': return <ScheduleIcon sx={{ color: '#ffc107' }} />;
      default: return <CheckCircleIcon sx={{ color: '#28a745' }} />;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <ScheduleIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Termene Procesuale
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="termene tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 150,
              padding: '14px 28px',
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '25px 25px 0 0',
              margin: '0 2px',
              transition: 'all 0.3s ease-in-out',
              border: '2px solid #e0e0e0',
              borderBottom: 'none',
              backgroundColor: '#fafafa',
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#424242',
                color: '#212121',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: '#424242',
                borderColor: '#424242',
                color: 'white',
                fontWeight: 600,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(66, 66, 66, 0.3)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
        >
          <Tab label="Termen Individual" />
          <Tab label="Termene Multiple" />
        </Tabs>
      </Box>

      {/* Tab pentru Termen Individual */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="body2" color="success.main">
                <strong>Calculator Individual:</strong> Calculează un singur termen procesual cu excluderea zilelor nelucrătoare și sărbătorilor legale.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data de referință <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataReferinta}
                  onChange={(date) => setDataReferinta(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip termen <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipTermen}
                    onChange={(e) => setTipTermen(e.target.value)}
                  >
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Civile
                    </MenuItem>
                    <MenuItem value="apel">Apel (30 zile)</MenuItem>
                    <MenuItem value="recurs">Recurs (15 zile)</MenuItem>
                    <MenuItem value="contestatie">Contestație (15 zile)</MenuItem>
                    <MenuItem value="opozitie">Opoziție (15 zile)</MenuItem>
                    <MenuItem value="revizuire">Cerere de revizuire (30 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Penale
                    </MenuItem>
                    <MenuItem value="apel_penal">Apel penal (10 zile)</MenuItem>
                    <MenuItem value="recurs_penal">Recurs penal (10 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Administrativ
                    </MenuItem>
                    <MenuItem value="contestatie_administrativa">Contestație act administrativ (30 zile)</MenuItem>
                    <MenuItem value="plangere_prealabila">Plângere prealabilă (30 zile)</MenuItem>
                    <MenuItem value="raspuns_plangere">Răspuns la plângere (45 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Comerciale
                    </MenuItem>
                    <MenuItem value="contestatie_executare">Contestație executare (15 zile)</MenuItem>
                    <MenuItem value="opozitie_ordonanta">Opoziție ordonanță (5 zile)</MenuItem>
                    <MenuItem value="anulare_arbitraj">Anulare arbitraj (90 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Fiscal
                    </MenuItem>
                    <MenuItem value="contestatie_fiscala">Contestație fiscală (30 zile)</MenuItem>
                    <MenuItem value="plangere_fiscala">Plângere fiscală (45 zile)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetCalculatorIndividual}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatTermen && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(null, 'individual')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatTermen)}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatTermen && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: `2px solid ${getStatusColor(rezultatTermen.statusTermen)}`,
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: getStatusColor(rezultatTermen.statusTermen),
                    marginBottom: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  {getStatusIcon(rezultatTermen.statusTermen)}
                  {rezultatTermen.tipTermen}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Tip proces:
                      </Typography>
                      <Chip label={rezultatTermen.tip} color="primary" size="small" />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Termen legal:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatTermen.numarZile} zile lucrătoare
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Data de referință:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatTermen.dataReferinta}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Data limită:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: getStatusColor(rezultatTermen.statusTermen) }}>
                        {rezultatTermen.dataLimita}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Status:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: getStatusColor(rezultatTermen.statusTermen) }}>
                        {rezultatTermen.zileRamase > 0 ? `${rezultatTermen.zileRamase} zile rămase` : 'EXPIRAT'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Baza legală:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#e3f2fd',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #90caf9',
                      color: '#1565c0'
                    }}
                  >
                    {rezultatTermen.bazaLegala}
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab pentru Termene Multiple */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.main">
                <strong>Calculator Multiplu:</strong> Calculează mai multe termene simultan pentru o dată de referință comună. Util pentru gestionarea mai multor dosare.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data de referință <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataReferintaMultiplu}
                  onChange={(date) => setDataReferintaMultiplu(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Termene selectate <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={termeneSelectate}
                    onChange={(e) => setTermeneSelectate(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={termene[value].descriere} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.entries(termene).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value.descriere} ({value.zile} zile) - {value.tip}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetCalculatorMultiplu}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultateMultiple.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultateMultiple, 'multiplu')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultateMultiple.length > 0 && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#495057',
                    marginBottom: 3
                  }}
                >
                  <CalendarTodayIcon sx={{ marginRight: 1 }} />
                  Termene Calculate ({rezultateMultiple.length})
                </Typography>
                
                <List>
                  {rezultateMultiple.map((rezultat, index) => (
                    <ListItem 
                      key={index}
                      sx={{ 
                        border: `1px solid ${getStatusColor(rezultat.statusTermen)}`,
                        borderRadius: 1,
                        marginBottom: 1,
                        backgroundColor: 'white'
                      }}
                    >
                      <ListItemIcon>
                        {getStatusIcon(rezultat.statusTermen)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {rezultat.tipTermen}
                            </Typography>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: getStatusColor(rezultat.statusTermen) 
                              }}
                            >
                              {rezultat.dataLimitaFormatata}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {rezultat.tip} • {rezultat.numarZile} zile lucrătoare • {rezultat.bazaLegala}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: getStatusColor(rezultat.statusTermen),
                                marginTop: 0.5
                              }}
                            >
                              {rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'}
                            </Typography>
                          </Box>
                        }
                      />
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copiazaRezultat(rezultat)}
                      >
                        Copiază
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalculatorTermene;